import type {
  OrgChatAttachmentInput,
  OrgChatPresetDataInput,
} from '@/components/org-chat/org-chat-types';
import { inferWorkspaceFileMimeType } from '@/components/org-chat/composer-upload-accept';
import { type AttachmentIdentityInput } from './reconcile-composer-upload-attachments';
import { USER_SKILL_CHIP_UPLOAD_ID_PREFIX } from './org-chat-api-attachment';
export const attachmentIdentityKeys = (
  attachment: AttachmentIdentityInput
): Array<string> => {
  const identities: Array<string> = [];
  if (attachment.workspaceFileId) {
    identities.push(`workspace:${String(attachment.workspaceFileId)}`);
  }
  if (attachment.vfsPath?.trim()) {
    identities.push(`vfs:${attachment.vfsPath.trim()}`);
  }
  if (attachment.storageKey?.trim()) {
    identities.push(`storage:${attachment.storageKey.trim()}`);
  }
  if (attachment.storageId) {
    identities.push(`storage-id:${String(attachment.storageId)}`);
  }
  for (const url of [attachment.url, attachment.previewUrl]) {
    if (url?.trim()) {
      identities.push(`url:${url.trim()}`);
    }
  }
  return identities;
};

export const dedupeAttachmentsByIdentity = <T extends AttachmentIdentityInput>(
  attachments: Array<T>
): Array<T> => {
  const identitySets = attachments.map(attachmentIdentityKeys);
  const parents = attachments.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parents[root] !== root) {
      root = parents[root] ?? root;
    }
    while (parents[index] !== index) {
      const parent = parents[index] ?? root;
      parents[index] = root;
      index = parent;
    }
    return root;
  };
  const union = (left: number, right: number): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) parents[rightRoot] = leftRoot;
  };
  const firstIndexByIdentity = new Map<string, number>();
  identitySets.forEach((identities, index) => {
    for (const identity of identities) {
      const previousIndex = firstIndexByIdentity.get(identity);
      if (previousIndex === undefined) {
        firstIndexByIdentity.set(identity, index);
      } else {
        union(previousIndex, index);
      }
    }
  });

  const retainedRoots = new Set<number>();
  return attachments.filter((_, index) => {
    const root = find(index);
    if (retainedRoots.has(root)) return false;
    retainedRoots.add(root);
    return true;
  });
};

export const includeSemanticDocumentAttachments = (
  attachments: Array<OrgChatAttachmentInput>,
  ...referenceGroups: Array<Array<OrgChatAttachmentInput>>
): Array<OrgChatAttachmentInput> => {
  const semanticDocuments = referenceGroups
    .flat()
    .map((reference) => ({
      reference,
      mimeType: inferWorkspaceFileMimeType({
        path: reference.filename,
        mimeType: reference.mimeType,
      }),
    }))
    .filter(
      ({ reference, mimeType }) =>
        reference.kind !== 'image' &&
        !mimeType.trim().toLowerCase().startsWith('image/')
    )
    .map(({ reference, mimeType }) => ({
      ...reference,
      kind: 'file' as const,
      mimeType,
    }));
  return dedupeAttachmentsByIdentity([...attachments, ...semanticDocuments]);
};

export function buildPaletteInstruction({
  colors,
  name,
  percentages,
}: {
  colors: Array<string>;
  name?: string | null;
  percentages?: Array<number | undefined>;
}): string {
  if (colors.length === 0) return '';
  const trimmedName = name?.trim();
  const hasCompleteDistribution =
    percentages?.length === colors.length &&
    percentages.every(
      (percentage) =>
        typeof percentage === 'number' && Number.isFinite(percentage)
    );
  const formattedColors = hasCompleteDistribution
    ? colors.map((color, index) => `${color} (${percentages![index]}%)`)
    : colors;
  const colorLabel = hasCompleteDistribution
    ? colors.length === 1
      ? 'color proportion'
      : 'color proportions'
    : colors.length === 1
      ? 'color'
      : 'colors';
  if (trimmedName) {
    if (colors.length === 1) {
      return `Use the custom palette "${trimmedName}" with this exact ${colorLabel}: ${formattedColors[0]}.`;
    }
    return `Use the custom palette "${trimmedName}" with these ${colors.length} exact ${colorLabel}: ${formattedColors.join(', ')}.`;
  }
  return `Use a ${colors.length}-color palette with these exact ${colorLabel}: ${formattedColors.join(', ')}.`;
}

export type SelectedUserSkillPayload = NonNullable<
  OrgChatPresetDataInput['selectedUserSkills']
>[number];

export function userSkillChipUploadId(path: string): string {
  return `${USER_SKILL_CHIP_UPLOAD_ID_PREFIX}${path}`;
}

export function isUserSkillVfsPath(vfsPath: string): boolean {
  const workspaceStorageSlug = vfsPath.split('/')[1] ?? '';
  if (!workspaceStorageSlug) return false;
  return (
    vfsPath.startsWith(`/${workspaceStorageSlug}/`) &&
    vfsPath.endsWith('/SKILL.md') &&
    !vfsPath.includes('/../') &&
    !vfsPath.includes('/./')
  );
}

export function extractSelectedUserSkillsFromFileChips(
  chips: Array<{ uploadId?: string; filename?: string; vfsPath?: string }>
): Array<SelectedUserSkillPayload> {
  return chips
    .map((chip) => {
      const uploadId = chip.uploadId?.trim() ?? '';
      const vfsPath = chip.vfsPath?.trim() ?? '';
      // This is a display-side classifier only. The server resolves the stable
      // catalog identity and applies live VFS/Drive authorization before use.
      const path = isUserSkillVfsPath(vfsPath)
        ? vfsPath
        : uploadId.startsWith(USER_SKILL_CHIP_UPLOAD_ID_PREFIX)
          ? uploadId.slice(USER_SKILL_CHIP_UPLOAD_ID_PREFIX.length).trim()
          : '';
      if (!path) {
        return null;
      }

      const title = chip.filename?.trim() ?? '';
      if (path.length === 0 || title.length === 0) {
        return null;
      }

      return { title, path };
    })
    .filter((skill): skill is SelectedUserSkillPayload => skill !== null);
}

export function mergeSelectedUserSkills(
  ...skillGroups: Array<Array<SelectedUserSkillPayload> | undefined>
): Array<SelectedUserSkillPayload> {
  const merged = new Map<string, SelectedUserSkillPayload>();
  for (const skills of skillGroups) {
    for (const skill of skills ?? []) {
      const path = skill.path.trim();
      const title = skill.title.trim();
      if (path.length === 0 || title.length === 0 || merged.has(path)) {
        continue;
      }
      merged.set(path, { title, path });
    }
  }
  return Array.from(merged.values());
}

export const hasItems = <T>(value: Array<T> | undefined): value is Array<T> =>
  Array.isArray(value) && value.length > 0;

export const WORKSPACE_NATIVE_ARTIFACT_EXTENSIONS = [
  '.sheet',
  '.op',
  '.questions',
  '.excalidraw',
] as const;

export const isWorkspaceNativeArtifactReference = (
  value: string | undefined
): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return WORKSPACE_NATIVE_ARTIFACT_EXTENSIONS.some((extension) =>
    normalized.endsWith(extension)
  );
};

export const uniqueStrings = (
  values: Array<string | undefined>
): Array<string> =>
  Array.from(
    new Set(
      values.filter(
        (value): value is string =>
          typeof value === 'string' && value.trim().length > 0
      )
    )
  );

export const getStablePreviewUrl = (
  value: string | undefined
): string | undefined => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};

export let clientInputSequence = 0;

export const createOrgChatClientInputId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  clientInputSequence += 1;
  return `org-chat-${Date.now()}-${clientInputSequence}`;
};
